import { isValidElement, cloneElement } from 'react';
import { reduce, map } from 'lodash';
import { Formik } from 'formik';
import { Typography } from '@mui/material';
import { Spacer } from '@client/components/common/Spacer';
import { Form } from '@client/components/forms/Form';
import { Hidden } from '@client/components/forms/Hidden';
import { TextField } from '@client/components/forms/TextField';
import { Modal } from './Modal';
import type { TextFieldProps, ButtonProps } from '@mui/material';
import type { GenericList } from '@sda/helpers/helpers.types';
import type { FormikConfig, FormikProps } from 'formik';
import type { ModalProps } from './Modal/ModalProps.interface';

interface PromptProps<T> extends ModalProps {
  readonly id?: string;
  readonly scope?: string;
  readonly buttonLabel?: string;
  readonly buttonSize?: ButtonProps['size'];
  readonly ButtonProps?: ButtonProps;
  readonly initialValues: T;
  readonly validate?: FormikConfig<T>['validate'];
  readonly validationSchema?: FormikConfig<T>['validationSchema'];
  readonly onSubmit?: FormikConfig<T>['onSubmit'];
  readonly inputProps?: TextFieldProps;
  readonly component?: React.ReactNode;
  readonly trackedValues?: string[];
}

export class Prompt {
  static show<T = GenericList<unknown>>({
    validationSchema,
    validate,
    initialValues,
    id = Modal.FORM_NAME,
    body = '',
    inputProps = {},
    onSubmit = () => {},
    onSuccess = () => {},
    component: Component,
    scope = 'dialog-button',
    trackedValues = [],
    buttonLabel = 'Submit',
    ButtonProps = {},
    buttonSize,
    backgroundColor,
    buttons,
    ...modalProps
  }: PromptProps<T>) {
    const keys = Object.keys(initialValues);

    const values = reduce(
      initialValues || {},
      (result, value, key) => {
        if (trackedValues.includes(key)) {
          return {
            ...result,
            [`old_${key}`]: value,
          };
        }

        return result;
      },
      initialValues,
    );

    let setSubmittingCallback: FormikProps<T>['setSubmitting'] = () => {};

    const content = (
      <Formik
        initialValues={values}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validate={validate}
        validateOnChange
      >
        {(formikProps) => {
          const { setSubmitting } = formikProps;
          setSubmittingCallback = setSubmitting;

          let FormComponent: React.ElementType<any> = TextField;

          if (Component) {
            FormComponent = Component as React.ElementType<any>;
          }

          return (
            <Form
              id={id}
              className={id}
            >
              {body && (
                <Typography color="textSecondary">
                  {body}

                  <Spacer />
                </Typography>
              )}

              {isValidElement(FormComponent) ? FormComponent : (
                <div>
                  <FormComponent
                    fullWidth
                    required
                    margin="normal"
                    {...inputProps}
                  />
                </div>
              )}

              {map(keys, (key) => {
                if (trackedValues.includes(key)) {
                  return (
                    <Hidden
                      key={key}
                      name={`old_${key}`}
                    />
                  );
                }

                return null;
              })}
            </Form>
          );
        }}
      </Formik>
    );

    Modal.show({
      ...modalProps,
      backgroundColor,
      body: content,
      buttons: buttons || [
        cloneElement(Modal.BUTTONS().SAVE, {
          scope,
          onError: () => setSubmittingCallback(false),
          children: buttonLabel,
          color: backgroundColor,
          className: !buttonSize ? 'mobileFullWidth' : buttonSize,
          size: buttonSize || 'medium',
          ButtonProps: {
            ...ButtonProps,
            form: id,
          },
          onSuccess: (data) => {
            Prompt.hide();
            onSuccess(data);
          },
        }) as any,
      ],
    });
  }

  static hide() {
    Modal.hide();
  }
}
